import { showFailToast, showSuccessToast } from "vant";
import { useI18n } from "vue-i18n";
import { getDealerDisplayAddress } from "@/views/home/maptiler/useMapTilerAllStores";

const isIOSDevice = () => {
  if (typeof navigator === "undefined") {
    return false;
  }
  return (
    /ipad|iphone|ipod/i.test(navigator.userAgent || "") ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const isLikelyTouchDevice = () => {
  if (typeof window === "undefined") {
    return false;
  }
  return (
    window.matchMedia?.("(pointer: coarse)")?.matches === true ||
    navigator.maxTouchPoints > 0
  );
};

/** 兼容 iOS / Android 平板浏览器的同步复制 */
export const legacyCopyText = (value) => {
  if (typeof document === "undefined") {
    return false;
  }

  const text = String(value || "").trim();
  if (!text) {
    return false;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.setAttribute("autocomplete", "off");
  textarea.setAttribute("autocapitalize", "off");
  textarea.setAttribute("spellcheck", "false");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.width = "2em";
  textarea.style.height = "2em";
  textarea.style.padding = "0";
  textarea.style.border = "none";
  textarea.style.outline = "none";
  textarea.style.boxShadow = "none";
  textarea.style.background = "transparent";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  textarea.style.zIndex = "-1";

  document.body.appendChild(textarea);

  try {
    if (isIOSDevice()) {
      textarea.contentEditable = true;
      textarea.readOnly = false;
      const range = document.createRange();
      range.selectNodeContents(textarea);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }

    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, text.length);

    return document.execCommand("copy");
  } catch (_) {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
};

export const copyTextToClipboard = async (text) => {
  const value = String(text || "").trim();
  if (!value) {
    return false;
  }

  if (isLikelyTouchDevice()) {
    if (legacyCopyText(value)) {
      return true;
    }
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch (_) {}
    }
    return false;
  }

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (_) {}
  }

  return legacyCopyText(value);
};

export const useMapTilerCopyText = () => {
  const { t } = useI18n();

  const showCopyResult = (ok) => {
    if (ok) {
      showSuccessToast(t("app.qrcoderegister.copysuccess"));
    } else {
      showFailToast(t("app.qrcoderegister.copyfail"));
    }
  };

  const copyText = (text) => {
    const value = String(text || "").trim();
    if (!value) {
      showCopyResult(false);
      return;
    }

    // 手机/平板：点击事件内同步复制，避免 async 丢失用户手势
    if (isLikelyTouchDevice()) {
      if (legacyCopyText(value)) {
        showCopyResult(true);
        return;
      }
      if (navigator.clipboard?.writeText) {
        navigator.clipboard
          .writeText(value)
          .then(() => showCopyResult(true))
          .catch(() => showCopyResult(false));
        return;
      }
      showCopyResult(false);
      return;
    }

    void copyTextToClipboard(value).then(showCopyResult);
  };

  const copyDealerAddress = (dealer) => {
    copyText(getDealerDisplayAddress(dealer));
  };

  return { copyText, copyDealerAddress };
};
